import { isIOS } from "react-device-detect";

interface DownloadSvgOptions {
  svgId: string;
  fileName: string;
  scaleFactor?: number;
}

const cloneSvg = (svgId: string): HTMLElement => {
  const svgElement = document.getElementById(svgId);

  if (!svgElement) {
    throw new Error(`SVG element with id '${svgId}' not found`);
  }

  return svgElement.cloneNode(true) as HTMLElement;
};

export const shareOrDownload = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  const file = new File([blob], fileName, { type: blob.type });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      throw error;
    }
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = fileName;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadSvg = async (
  svgId: string,
  fileName: string,
): Promise<void> => {
  const cloned = cloneSvg(svgId);

  const svgData = new XMLSerializer().serializeToString(cloned);
  const blob = new Blob([svgData], { type: "image/svg+xml" });

  if (isIOS) {
    await shareOrDownload(blob, fileName);
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = fileName;
  link.href = url;
  link.click();

  URL.revokeObjectURL(url);
};

export const downloadSvgAsPng = async ({
  svgId,
  fileName,
  scaleFactor = 3,
}: DownloadSvgOptions): Promise<void> => {
  const cloned = cloneSvg(svgId);
  const svgData = new XMLSerializer().serializeToString(cloned);

  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context is null"));
      return;
    }

    const img = new Image();

    img.onload = async () => {
      canvas.width = img.width * scaleFactor;
      canvas.height = img.height * scaleFactor;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      try {
        if (isIOS) {
          const blob = await fetch(canvas.toDataURL("image/png")).then((r) =>
            r.blob(),
          );
          await shareOrDownload(blob, fileName);
        } else {
          const link = document.createElement("a");
          link.download = fileName;
          link.href = canvas.toDataURL("image/png");
          link.click();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Image failed to load for QR conversion"));
    };

    img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  });
};
